import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from '@/alerts/AlertProvider';
import { CreateVehicleSmartInsightResponse } from '@/api/requests';
import { QueryParams } from '@/api/requests/monitoring';
import { useAppConstantsContext } from '@/context/AppConstants';
import { useLLM } from '@/hooks/useLLM';
import { useReportMonitors } from '@/monitoring/useReportMonitors';
import { mapReportParamsToReportTypes } from '@/reports/reportGetters/reportGetters';
import { CarouselBottomArrowNav } from '@/reports/shared/CarouselBottomArrowNav/CarouselBottomArrowNav';
import { BaseReportOverview } from '@/reports/shared/ReportOverview/BaseReportOverview';
import { SerializedVehicleReport } from '@/reports/types/serialized';
import {
  Button,
  Box,
  Text,
  Divider,
  Icons,
  SxProps,
  Modal,
} from '@/theme/index';
import {
  DateUtil,
  commaFormat,
  commaFormatCurrency,
  useScreenSize,
} from '@/utils/index';
import { VINInformation } from './VINInformation/VINInformation';
import { VehicleReportInfoCarousel } from './VehicleReportInfoCarousel/VehicleReportInfoCarousel';
import { VehicleReportInfoText } from './VehicleReportInfoText/VehicleReportInfoText';
import { DataListItem } from './components';
import { PremiumDataCta } from '../PremiumDataCta/PremiumDataCta';
import { PremiumDataCtaVariation } from '../PremiumDataCta/PremiumDataCtaVariation';
import { useRerunVehicleReport } from '../hooks/useRerunVehicleReport/useRerunVehicleReport';

interface VehicleOverviewProps {
  reportType: string;
  vehicleReportData: SerializedVehicleReport;
  permalink: string;
  nmvtisSuccess: boolean | undefined;
  reportUpgraded: boolean;
  handleFreeUpgrade: () => void;
  badge?: JSX.Element;
  containerStyling?: SxProps;
  showVehicleReportInfoCarousel?: boolean;
}

export function VehicleOverview({
  reportType,
  vehicleReportData,
  permalink,
  nmvtisSuccess,
  reportUpgraded,
  handleFreeUpgrade,
  badge,
  showVehicleReportInfoCarousel,
  containerStyling,
}: VehicleOverviewProps) {
  // HOOKS
  const { reportIsMonitored, toggleReportIsMonitored } =
    useReportMonitors(permalink);
  const { isTablet, isMobile } = useScreenSize();
  const rerunReport = useRerunVehicleReport();
  const { data: llmResponse, isLoading } = useLLM('vehicle');
  const {
    images: { imgNoVehicleImageProvided },
  } = useAppConstantsContext();

  // STATE
  const { meta } = vehicleReportData;
  const [historyBasedValue, setHistoryBasedValue] = useState('$0');
  const [showImagesModal, setShowImagesModal] = useState(false);

  // CONSTANTS
  const isInUnlockTitlesVariation = useFeatureIsOn('bump-3416');
  const isInVHRValuationVariation = useFeatureIsOn('bump-3879');
  const currentVehicle = vehicleReportData.automobiles[0];
  const images = currentVehicle?.images;
  const updatedAt =
    vehicleReportData.updated_at || vehicleReportData.created_at;
  const dateParser = new DateUtil();
  const updatedDate =
    dateParser.parseDateFromString(
      updatedAt,
      'L/dd/yyyy hh:mm:ss a',
      'yyyy-MM-dd HH:mm:ss ZZZ'
    ) || '';
  const newDate = updatedDate.split(' ');
  newDate.splice(1, 0, 'at');
  const formattedDate = newDate.join(' ');
  const isYMM = vehicleReportData.meta?.search_by === 'ymm';
  const isVIN = vehicleReportData.meta?.search_by === 'vin';
  const mileage = commaFormat(currentVehicle?.mileage?.miles);
  const typedLLMResponse = llmResponse as CreateVehicleSmartInsightResponse;
  const valuation = currentVehicle?.valuation || 0;
  const marketValue = valuation ? valuation.clean_retail?.adjusted : 0;
  const dataList: [string | JSX.Element, string | JSX.Element][] = [];
  const tableStyles = {
    table: {
      marginTop: '1rem',

      '.MuiTypography-root': {
        fontSize: '1.2rem',
        fontWeight: 'normal',
      },
    },
  };

  // LIFE CYCLE
  useEffect(() => {
    if (!isLoading && typedLLMResponse?.data) {
      setHistoryBasedValue(
        commaFormatCurrency(
          parseInt(typedLLMResponse.data.historyBasedValue.toFixed(2) || '', 10)
        )
      );
    }
  }, [isLoading, typedLLMResponse?.data]);

  // FUNCTIONS
  const onMonitorSuccess = () => {
    enqueueSnackbar(
      `Monitor was ${reportIsMonitored ? 'disabled' : 'enabled'}`,
      {
        variant: 'success',
      }
    );
  };

  const toggleModal = () => {
    setShowImagesModal(!showImagesModal);
  };

  function handleMonitorClick() {
    const queryParams: QueryParams = {
      'report_monitor[is_claimed]': 'false',
      'report_monitor[report_type]':
        mapReportParamsToReportTypes[reportType.toLowerCase()] || '',
    };
    toggleReportIsMonitored({
      queryParams,
      successCallback: () => onMonitorSuccess(),
    });
  }

  if (mileage) {
    dataList.push([
      DataListItem(
        <strong>{mileage}</strong>,
        <Icons.SpeedRounded sx={{ color: 'progress.light' }} />,
        '',
        '',
        isMobile
      ),
      'Last reported mileage',
    ]);
  }

  if (marketValue) {
    dataList.push([
      DataListItem(
        <strong>
          {commaFormatCurrency(parseInt(marketValue?.toFixed(2) || '', 10))}
        </strong>,
        <Icons.SellOutlined sx={{ color: 'progress.light' }} />,
        isInVHRValuationVariation ? 'vehicle-value' : 'vehicle-market',
        '',
        isMobile
      ),
      DataListItem(
        'Market Value',
        undefined,
        '',
        'Market values is an estimate of what the vehicle is valued at based on recent sales of similar vehicles.',
        isMobile
      ),
    ]);
  } else if (historyBasedValue !== '$0') {
    dataList.push([
      DataListItem(
        <strong>{historyBasedValue}</strong>,
        <Icons.SellOutlined sx={{ color: 'progress.light' }} />,
        '',
        '',
        isMobile
      ),
      DataListItem(
        'History-based Value',
        undefined,
        '',
        'Estimates may vary from actual values. This tool is intended for informational use only and should not replace professional assessments.',
        isMobile
      ),
    ]);
  }

  const monitorAndClaimButtons = () => {
    return (
      <Button
        className={`overview-monitor-vehicle ${
          reportIsMonitored ? 'monitored' : ''
        }`}
        variant={reportIsMonitored ? 'outlined' : 'contained'}
        sx={{ mt: 3, p: 1, width: { xs: '100%', md: '182px' } }}
        onClick={handleMonitorClick}
      >
        {reportIsMonitored ? (
          <Icons.NotificationsRounded />
        ) : (
          <Icons.NotificationsOutlined sx={{ color: 'primary.contrastText' }} />
        )}
        <Text fontSize="0.94rem" ml={1}>
          {reportIsMonitored ? 'Monitored' : 'Monitor Vehicle'}
        </Text>
      </Button>
    );
  };

  const displayImages = () => {
    return (
      <Box width="100%" paddingLeft={2}>
        {images.length === 0 && (
          <img
            src={imgNoVehicleImageProvided}
            alt="No vehicle placeholder"
            width="275px"
            height="300px"
          />
        )}
        {images.length > 0 && (
          <CarouselBottomArrowNav
            height={304}
            width="100%"
            images={images}
            toggleModal={toggleModal}
          />
        )}
        <Box width="100%">
          <Text textAlign="center" variant="body2">
            These images can be examples only and not images of the exact
            vehicle. Logos shown are registered trademarks of their respective
            owners.
          </Text>
        </Box>
      </Box>
    );
  };

  const displayDesktopImages = () => {
    if (!isTablet) {
      return displayImages();
    }
  };

  const displayMobileImages = () => {
    if (isTablet) {
      return displayImages();
    }
  };

  const handleRerunClick = () => {
    if (!meta) return;
    enqueueSnackbar('Currently rerunning the report!', {
      variant: 'success',
    });
    rerunReport(meta);
  };

  const subInformation = () => {
    if (!isYMM && currentVehicle?.vehicleYMM) {
      return (
        <VINInformation
          metaName={
            <Text
              variant="body2"
              fontWeight="bold"
              m={0}
              sx={{
                color: 'progress.light',
              }}
            >
              {isVIN ? `VIN: ${meta?.metaName}` : `${meta?.metaName}`}
            </Text>
          }
          isClassicVin={currentVehicle.isClassicVin}
          vinDecodeConfidenceScore={currentVehicle.vinDecodeConfidenceScore}
        />
      );
    }
    return '';
  };

  return (
    <BaseReportOverview
      title={currentVehicle?.vehicleYMM || meta?.metaName || ''}
      titleProps={{ color: 'primary' }}
      subTitle={subInformation()}
      dataListItems={dataList}
      tableStyles={tableStyles}
      reportType={reportType}
      claimButton={monitorAndClaimButtons()}
      showHighConfidenceToggle={false}
      showHighConfidenceDataOnly={false}
      imageCarousel={displayDesktopImages()}
      setShowHighConfidenceDataOnly={function (): void {
        throw new Error('Function not implemented.');
      }}
      alternatingColorRows={false}
      badge={badge}
      containerStyling={containerStyling}
    >
      <Divider sx={{ mt: 2 }} />
      {!isYMM && !showVehicleReportInfoCarousel && (
        <VehicleReportInfoCarousel
          vehicleReportData={vehicleReportData}
          handleFreeUpgrade={handleFreeUpgrade}
        />
      )}
      <VehicleReportInfoText
        updatedDate={formattedDate}
        alertMe={reportIsMonitored}
        handleMonitoringClick={handleMonitorClick}
        handleRerunClick={handleRerunClick}
      />
      <Divider />
      <>{displayMobileImages()}</>
      {!isYMM && (!reportUpgraded || !nmvtisSuccess) && (
        <Box mt={3}>
          {isInUnlockTitlesVariation ? (
            <PremiumDataCtaVariation
              nmvtisSuccess={nmvtisSuccess}
              reportUpgraded={reportUpgraded}
              handleFreeUpgrade={handleFreeUpgrade}
            />
          ) : (
            <PremiumDataCta
              isSection={false}
              nmvtisSuccess={nmvtisSuccess}
              reportUpgraded={reportUpgraded}
              handleFreeUpgrade={handleFreeUpgrade}
            />
          )}
        </Box>
      )}
      <Modal
        open={showImagesModal}
        onClose={toggleModal}
        sx={{
          '.MuiDialog-paper': {
            maxWidth: '900px',
          },
        }}
      >
        <CarouselBottomArrowNav
          height={isMobile ? 304 : 404}
          width="100%"
          images={images}
        />
      </Modal>
    </BaseReportOverview>
  );
}
